/**
 * Recarrega o painel quando um chunk da versão antiga deixou de existir.
 *
 * O sintoma, para quem usa: a pessoa deixa o painel aberto, sai deploy, e
 * a próxima navegação simplesmente não acontece — tela em branco ou
 * congelada. No console aparece "Failed to fetch dynamically imported
 * module" e um erro de MIME "text/html", que confunde porque parece
 * problema de servidor.
 *
 * É o oposto: o servidor está certo. Cada build gera nomes com hash
 * (`agenda-BLPEbFMN.js`), e o deploy publica um conjunto novo. A aba
 * aberta ainda tem o índice ANTIGO na memória e pede um arquivo que não
 * existe mais; o Cloudflare Pages, como toda SPA, devolve o `index.html`
 * para caminho desconhecido — daí o "esperava módulo, veio text/html".
 *
 * Recarregar resolve porque traz o índice novo. O que não pode acontecer é
 * recarregar em laço: se a falha for outra (rede caída, asset corrompido),
 * o reload não resolve nada e a pessoa fica presa num pisca-pisca. Por
 * isso a tentativa é registrada em `sessionStorage` e vale UMA vez por
 * sessão de aba — na segunda falha seguida, deixamos o erro aparecer, que
 * é o comportamento honesto.
 *
 * `sessionStorage` e não `localStorage` de propósito: o marcador precisa
 * morrer junto com a aba. Em `localStorage`, uma pessoa que passou por
 * isso hoje ficaria sem a proteção no próximo deploy.
 */
const RELOAD_MARKER = "agendo:stale-build-reload";

export function installStaleBuildReload() {
  // O evento é do Vite e cobre o preload de rota (o `defaultPreload:
  // "intent"` do router dispara import antes do clique). O
  // `unhandledrejection` cobre o import que acontece no clique em si, que
  // não passa pelo preload — os dois caminhos levam ao mesmo sintoma.
  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    reloadOnce();
  });

  window.addEventListener("unhandledrejection", (event) => {
    if (isStaleChunkError(event.reason)) {
      event.preventDefault();
      reloadOnce();
    }
  });

  // Chegou até aqui inteiro: a próxima falha merece um reload de novo.
  // Limpar só depois do primeiro render evita apagar a marca durante o
  // próprio carregamento que estamos tentando salvar.
  window.addEventListener("load", () => {
    try {
      sessionStorage.removeItem(RELOAD_MARKER);
    } catch {
      // Aba anônima com storage bloqueado: sem marcador, o pior caso é
      // não recarregar — nunca recarregar em laço.
    }
  });
}

/**
 * A mensagem varia por navegador, então o teste é por trecho e não por
 * igualdade. O erro de MIME entra na lista porque é a forma que o Chrome
 * dá ao mesmo problema quando o servidor responde o `index.html`.
 */
function isStaleChunkError(reason: unknown): boolean {
  const message =
    reason instanceof Error ? reason.message : typeof reason === "string" ? reason : "";
  return (
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("error loading dynamically imported module") ||
    message.includes("Importing a module script failed") ||
    message.includes("Expected a JavaScript-or-Wasm module script")
  );
}

function reloadOnce() {
  try {
    if (sessionStorage.getItem(RELOAD_MARKER)) return;
    sessionStorage.setItem(RELOAD_MARKER, "1");
  } catch {
    // Sem storage não dá pra garantir "uma vez só", e recarregar às cegas
    // arrisca o laço. Deixa o erro aparecer.
    return;
  }
  window.location.reload();
}
